import { merge } from "lodash";
import { useState } from "react";
import {
  getMerchantOperationMultisigByTab,
  MerchantOperationApiPath,
} from "src/api/merchant";
import { OpRedirect } from "src/assets/icons";
import { Tooltip } from "src/components/MuiGenerals";
import OpIconButton from "src/components/OpIconButton";
import { useDialogProviderContext } from "src/features/DialogProvider";
import { useTranslation } from "src/hooks";
import { MultisigStatus, SignatureResultCode } from "src/utils/constant";
import { toDisplayTime } from "src/utils/helper";
import { useZusDialogStore } from "src/zustand/store";
import { translateRemark } from "../../../MerchantOperation";
import { IoperationTab } from "../../../types/IoperationTab";
import MultisigDialog from "./MultisigDialog";
import {
  MultisigData,
  MultisigDisplayData,
  Transactionbatchsignature,
} from "./MultisigDialog/types";

const TAB_API_PATH: Record<IoperationTab, MerchantOperationApiPath> = {
  all: "getAll",
  api_withdraw_tab: "apiWithdraw",
  portal_withdraw_tab: "portalWithdraw",
  sweep_tab: "sweep",
  swap_tab: "sweep",
  contract_sweep: "contractSweep",
};

function MultisigCellContent({
  rowData,
  currentTab,
}: {
  rowData: any;
  currentTab: IoperationTab;
}) {
  const { setIsOpen } = useDialogProviderContext();
  const zusDialog = useZusDialogStore();

  const [data, setData] = useState<MultisigDisplayData | undefined>(undefined);
  const { t } = useTranslation("enumConstants");

  const getMultisigData = async () => {
    zusDialog.openExtra("loadingDialog");

    const res = await getMerchantOperationMultisigByTab({
      batchId: rowData.batch_id,
      tabPath: TAB_API_PATH[currentTab],
    });

    zusDialog.closeExtra();

    if (!res) {
      return;
    }

    const getDatesDisplayData = (
      obj: MultisigData | Transactionbatchsignature,
    ) => ({
      created_date: toDisplayTime(obj.created_date),
      last_modified_date: toDisplayTime(obj.last_modified_date),
    });

    const displayMultisignStatus =
      t(MultisigStatus[res.multisign_status]) || res.multisign_status;

    const getSignatureDisplayData = (signature: Transactionbatchsignature) => ({
      ...signature,
      result_code:
        t(SignatureResultCode[signature.result_code]) || signature.result_code,
      is_verified: t(signature.is_verified ? "yes" : "no"),
      ...getDatesDisplayData(signature),
    });

    const displaySignerAddresses = JSON.parse(res.signer_addresses || "[]");

    const displayRemarks = translateRemark(res.remarks || "");

    const displayCompleted_sig = res.completed_sig || 0;

    const displayRequired_sig = res.required_sig || 0;

    const reassignedRes = merge(res, {
      ...getDatesDisplayData(res),
      multisign_status: displayMultisignStatus,
      raw_multisign_status: res.multisign_status,
      transaction_batch_signatures: res.transaction_batch_signatures?.map(
        getSignatureDisplayData,
      ),
      signer_addresses: displaySignerAddresses,
      completed_sig: displayCompleted_sig,
      required_sig: displayRequired_sig,
      remarks: displayRemarks,
    });

    setData(reassignedRes);
  };

  const onClick = async () => {
    await getMultisigData();
    setIsOpen(true);
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <Tooltip title={rowData.multisig}>
        <div>{rowData.multisig}</div>
      </Tooltip>
      <OpIconButton size="1rem" title={""} url={OpRedirect} onClick={onClick} />

      {data && <MultisigDialog data={data} refetchFn={getMultisigData} />}
    </div>
  );
}

export default MultisigCellContent;
